import { useState, useEffect } from "react";
import { api } from "@/App";
import CustomerLayout from "@/components/layout/CustomerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Ticket, Copy, CheckCircle, Clock, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const Vouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    try {
      const response = await api.get("/customer/vouchers");
      setVouchers(response.data);
    } catch (error) {
      toast.error("Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Code copied to clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const filterVouchers = (status) => {
    if (status === 'all') return vouchers;
    return vouchers.filter(v => v.status === status);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return <span className="badge badge-success"><CheckCircle size={12} className="mr-1" /> Active</span>;
      case 'used':
        return <span className="badge badge-gold"><Clock size={12} className="mr-1" /> Used</span>;
      case 'expired':
        return <span className="badge badge-error"><XCircle size={12} className="mr-1" /> Expired</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  if (loading) {
    return (
      <CustomerLayout title="My Vouchers">
        <div className="space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="h-48 rounded-xl animate-shimmer" />
          ))}
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout title="My Vouchers">
      <div className="space-y-6 animate-fade-in">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="stat-card text-center">
            <p className="stat-value text-[#4ADE80]">{filterVouchers('active').length}</p>
            <p className="stat-label">Active</p>
          </div>
          <div className="stat-card text-center">
            <p className="stat-value text-[#D4AF37]">{filterVouchers('used').length}</p>
            <p className="stat-label">Used</p>
          </div>
          <div className="stat-card text-center">
            <p className="stat-value text-[#EF4444]">{filterVouchers('expired').length}</p>
            <p className="stat-label">Expired</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#131316] border border-[#2A2A2F] p-1">
            <TabsTrigger 
              value="active" 
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]"
            >
              Active
            </TabsTrigger>
            <TabsTrigger 
              value="used"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]"
            >
              Used
            </TabsTrigger>
            <TabsTrigger 
              value="expired"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]"
            >
              Expired
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {filterVouchers(activeTab).length > 0 ? (
              <div className="space-y-4">
                {filterVouchers(activeTab).map((voucher) => {
                  const daysRemaining = getDaysRemaining(voucher.expires_at);
                  const isExpiringSoon = voucher.status === 'active' && daysRemaining <= 7;
                  
                  return (
                    <Card 
                      key={voucher.id} 
                      className={`bg-[#131316] border-[#2A2A2F] overflow-hidden ${
                        voucher.status !== 'active' ? 'opacity-70' : ''
                      }`}
                      data-testid={`voucher-card-${voucher.id}`}
                    >
                      <CardContent className="p-0">
                        <div className="p-4 md:p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-white font-semibold text-lg">
                                {voucher.reward_name || 'Reward Voucher'}
                              </h3>
                              <p className="text-[#6B6B6B] text-sm">Issued {formatDate(voucher.issued_at)}</p>
                            </div>
                            {getStatusBadge(voucher.status)}
                          </div>
                          
                          {/* Voucher Code */}
                          <div className="flex items-center gap-3 mb-4">
                            <div className="flex-1 voucher-code text-center">
                              {voucher.code}
                            </div>
                            {voucher.status === 'active' && (
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={() => copyCode(voucher.code)}
                                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0B]"
                                data-testid={`copy-btn-${voucher.id}`}
                              >
                                {copiedCode === voucher.code ? (
                                  <CheckCircle size={18} />
                                ) : (
                                  <Copy size={18} />
                                )}
                              </Button>
                            )}
                          </div>
                          
                          {/* Expiry Info */}
                          <div className={`flex items-center justify-between p-3 rounded-lg ${
                            isExpiringSoon ? 'bg-[#F59E0B]/10' : 'bg-[#0A0A0B]'
                          }`}>
                            <div className="flex items-center gap-2">
                              {isExpiringSoon && <AlertCircle size={16} className="text-[#F59E0B]" />}
                              <span className={isExpiringSoon ? 'text-[#F59E0B]' : 'text-[#A0A0A0]'}>
                                {voucher.status === 'active' 
                                  ? (isExpiringSoon 
                                      ? `Expires in ${daysRemaining} days!` 
                                      : `Valid until ${formatDate(voucher.expires_at)}`
                                    )
                                  : voucher.status === 'used'
                                    ? `Used on ${formatDate(voucher.used_at)}`
                                    : `Expired on ${formatDate(voucher.expires_at)}`
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <Ticket size={64} className="mx-auto mb-4 text-[#6B6B6B] opacity-50" />
                <h3 className="text-white text-xl font-semibold mb-2">
                  No {activeTab} vouchers
                </h3>
                <p className="text-[#A0A0A0]">
                  {activeTab === 'active' 
                    ? 'Redeem rewards to get vouchers!' 
                    : `Your ${activeTab} vouchers will appear here`
                  }
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </CustomerLayout>
  );
};

export default Vouchers;
